<template>
  <admin-input-list-wrapper :is-fetched="isFetched">
    <template #button>
      <v-row no-gutters>
        <v-col cols="auto">
          <base-button translation-path="button.addDict" @click="addDict"></base-button>
        </v-col>
        <v-col cols="auto">
          <dots-menu :items="importItems" @fromCsv="importDict('csv')" @fromDataSource="importDict('attribute')">
            <template #activator="{ on }">
              <base-button translation-path="button.importDictionary" v-on="on" />
            </template>
          </dots-menu>
        </v-col>
      </v-row>
    </template>
    <template #list>
      <v-list dense>
        <v-list-item
          v-for="dict in searchedItems"
          :key="dict.id"
          class="layer-element px-2 hover-list-element"
          style="height: 45px"
        >
          <v-list-item-content>
            <v-list-item-title v-text="dict.name" class="text-body-2 font-weight-regular" />
          </v-list-item-content>
          <v-list-item-action class="ml-0">
            <dots-menu
              font-size="19px"
              :items="getDotsItems(dict.read_only)"
              @showValues="editDict(dict)"
              @edit="editDict(dict)"
              @delete="showDeleteDictDialog(dict.id)"
              @exportCsv="exportDict(dict)"
            ></dots-menu>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </template>
    <template #dialogs>
      <dictionaries-editing
        v-if="isEditingDictLoaded"
        :is-visible.sync="isEditingDictVisible"
        :current-dict="currentDict"
      />
      <main-dialog
        :is-visible.sync="isDeleteDictVisible"
        v-if="isDeleteDictLoaded"
        :title="$i18n.t('dialog.deleteDictionary')"
        :body="$i18n.t('dialog.confirmDictionaryDelete')"
        agree-text="dialog.agreeDelete"
        @agree="handleDeleteDict(idToDelete)"
      ></main-dialog>
      <main-input-dialog
        dialog-width="40%"
        v-if="isImportDictLoaded"
        :is-visible.sync="isImportDictVisible"
        :loading="isImportDictLoading"
        :title-translation="
          $i18n.t(`dialog.dictionaries.loadValuesFrom${importDictType === 'attribute' ? 'DataSource' : 'Csv'}`)
        "
        agree-text="dialog.agreeLoad"
        @agree="handleImportDict"
        :inputs="importInput"
        @modalClosed="onImportClose"
      >
        <template v-for="input of importInput" #[`${input.name}.input`]="data">
          <v-col :key="input.name" cols="6">
            <data-input v-model="payload[input.name]" v-bind="data" @change="onInputValueChange(input.name, $event)" />
          </v-col>
        </template>
        <template #file.input>
          <file-input ref="file" :options="importFileOptions" :show-added-files="true" />
        </template>
      </main-input-dialog>
    </template>
    <template #search>
      <data-input
        :data-type="{ name: 'text' }"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$i18n.t('default.find')"
        v-model="search"
        clearable
        height="32"
        hide-details
        class="searchInput"
      >
      </data-input>
    </template>
  </admin-input-list-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';
import DotsMenu from '@/components/DotsMenu';
import AdminInputListWrapper from '@/components/AdminInputListWrapper';
import DataInput from '@/components/DataInput';
import FileInput from '@/components/FileInput';

export default {
  name: 'DictionariesSettingsList',
  components: {
    DataInput,
    DotsMenu,
    AdminInputListWrapper,
    DictionariesEditing: () => import('@/components/DictionariesEditing'),
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    FileInput,
  },
  data: () => ({
    allowedDictsTypes: ['text', 'integer', 'decimal', 'datetime'],
    currentDataSourceAttributes: undefined,
    dataSourcesFetched: false,
    dataSourcesAttributesFetched: false,
    search: '',
    isEditingDictVisible: false,
    isEditingDictLoaded: false,
    isDeleteDictVisible: false,
    isDeleteDictLoaded: false,
    currentDict: {},
    idToDelete: undefined,
    isFetched: false,
    isImportDictLoaded: false,
    isImportDictVisible: false,
    isImportDictLoading: false,
    importDictType: undefined,
    payload: {},
  }),
  computed: {
    token: get('authentication/token'),
    dataSources: get('layers/metadata'),
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources);
    },
    dataTypes: get('layers/dataTypes'),
    dicts: get('admin/dictionaries'),
    allowedDataTypes() {
      return this.dataTypes.filter(dT => this.allowedDictsTypes.includes(dT.name));
    },
    importFileOptions() {
      return {
        url: `${process.env.VUE_APP_API_URL}/url`,
        method: 'post',
        autoProcessQueue: false,
        uploadMultiple: false,
        addRemoveLinks: true,
        paramName: 'file',
        maxFiles: 1,
        acceptedFiles: '.csv',
        init: function() {
          this.on('maxfilesexceeded', file => {
            this.removeAllFiles();
            this.addFile(file);
          });
        },
      };
    },
    importItems() {
      return [
        {
          name: 'fromCsv',
        },
        {
          name: 'fromDataSource',
        },
      ];
    },
    importInput() {
      return this.importDictType ? this.importInputs[this.importDictType] : [];
    },
    importInputs() {
      return {
        attribute: [
          {
            name: 'name',
            translationPath: 'dialog.dictionaries.dictionaryName',
            dataType: { name: 'text' },
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
          },
          {
            name: 'data_source',
            translationPath: 'dialog.dataSource',
            subtitle: 'dialog.dictionaries.dictionaryDataSourceSubtitle',
            dataType: { name: 'select' },
            items: this.dataSourcesArray,
            itemValue: 'name',
            itemText: 'verbose_name',
            disabled: !this.dataSourcesFetched,
            loading: !this.dataSourcesFetched,
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
          },
          {
            name: 'attribute',
            translationPath: 'dialog.attribute',
            subtitle: 'dialog.dictionaries.dictionaryAttributeSubtitle',
            dataType: { name: 'select' },
            items: this.currentDataSourceAttributes,
            itemText: 'verbose_name',
            returnObject: true,
            disabled: !this.dataSourcesAttributesFetched,
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
          },
        ],
        csv: [
          {
            name: 'name',
            translationPath: 'dialog.dictionaries.dictionaryName',
            dataType: { name: 'text' },
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            value: '',
          },
          {
            name: 'data_type',
            translationPath: 'dialog.dictionaries.dictionaryDataType',
            dataType: { name: 'select' },
            items: this.allowedDataTypes,
            itemText: 'verbose_name',
            itemValue: 'name',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            value: '',
          },
          {
            name: 'file',
            translationPath: 'dialog.file',
            subtitle: 'dialog.dictionaries.dictionaryFileSubtitle',
            dataType: { name: 'text' },
            cols: '12',
            value: '',
          },
        ],
      };
    },
    searchedItems() {
      if (!this.search) {
        return this.sortItems();
      }
      return Object.values(this.sortItems()).filter(dict =>
        dict.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
  },
  methods: {
    getDataTypes: call('layers/getDataTypes'),
    getDataSources: call('layers/getLayersMetadata'),
    getDicts: call('admin/getDictionaries'),
    importDictionary: call('admin/importDictionary'),
    deleteDict: call('admin/deleteDictionary'),
    postDict: call('admin/postDictionary'),
    getDotsItems(isReadOnly) {
      return isReadOnly
        ? [{ name: 'showValues' }, { name: 'exportCsv' }]
        : [{ name: 'edit' }, { name: 'exportCsv' }, { name: 'delete' }];
    },
    addDict() {
      this.currentDict = {
        name: '',
        colors: [],
        pictures: [],
        picturesUrls: [],
        values: [],
        isNew: true,
        data_type: 'text',
      };
      this.isEditingDictVisible = true;
      this.isEditingDictLoaded = true;
    },
    importDict(type) {
      this.importDictType = type;
      this.isImportDictLoaded = true;
      this.isImportDictVisible = true;
    },
    exportDict(dict) {
      const { name, values } = dict;
      const fileContent = values.join(`\n`);
      const file = new Blob([fileContent], { type: 'text/csv;charset=utf-8;' });
      this.$_saveFile(URL.createObjectURL(file), `${name}.csv`);
    },
    getFormattedUniqueValues(values, type) {
      if (type === 'text') {
        return values;
      } else if (type === 'integer') {
        return values.map(value => parseInt(value)).filter(value => value || value === 0);
      } else if (type === 'decimal') {
        return values.map(value => parseFloat(value)).filter(value => value || value === 0);
      } else {
        return values
          .map(value => {
            const datejs = this.$dayjs(value, 'L LT');
            if (!datejs.isValid()) {
              return false;
            }
            const date = datejs.format('YYYY-MM-DD');
            const time = datejs.format('HH:mm:ss.000');
            return `${date}T${time}`;
          })
          .filter(value => !!value);
      }
    },
    async getFileText(file) {
      return await file.text();
    },
    async handleImportDict() {
      this.isImportDictLoading = true;
      try {
        if (this.importDictType === 'attribute') {
          const data = {
            attribute: this.payload.attribute.id,
            data_type: this.payload.attribute.data_type.name,
            datasource: this.payload.data_source,
            name: this.payload.name,
          };
          await this.importDictionary(data);
          this.isImportDictLoading = false;
          this.isImportDictVisible = false;
          this.onImportClose();
        } else {
          const files = this.$refs.file.$refs.dropzone.getAcceptedFiles();
          if (files.length < 1) {
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: this.$i18n.t('errors.filesRequired'),
            });
          } else {
            const file = files[0];
            const fileText = await this.getFileText(file);
            const splitFileText = fileText.split(/\r?\n/);
            const values = splitFileText.filter(value => value || value === 0);
            const uniqueValues = this.$_getUniqueArrayValues(values);
            const formattedUniqueValues = this.getFormattedUniqueValues(uniqueValues, this.payload.data_type);

            const emptyFile = new File([''], '');
            const colors = formattedUniqueValues.map(() => null);
            const pictures = formattedUniqueValues.map(() => emptyFile);

            const formData = new FormData();
            formData.append('name', this.payload.name);
            formData.append('data_type', this.payload.data_type);
            formData.append('values', JSON.stringify(formattedUniqueValues));
            formData.append('colors', JSON.stringify(colors));
            for (let picture of pictures) {
              formData.append('files', picture);
            }
            await this.postDict(formData);
            this.isImportDictLoading = false;
            this.isImportDictVisible = false;
            this.onImportClose();
          }
        }
      } catch {
        this.isImportDictLoading = false;
      }
    },
    onInputValueChange(inputName, value) {
      if (inputName === 'data_source') {
        this.payload.attribute = undefined;
        this.currentDataSourceAttributes = this.dataSources[value]?.attributes_schema.attributes;
        this.dataSourcesAttributesFetched = true;
      }
    },
    onImportClose() {
      this.dataSourcesAttributesFetched = false;
      this.importDictType = undefined;
      this.payload = {};
      this.$refs.file?.$refs.dropzone?.removeAllFiles();
    },
    showDeleteDictDialog(id) {
      this.idToDelete = id;
      this.isDeleteDictVisible = true;
      this.isDeleteDictLoaded = true;
    },
    handleDeleteDict(id) {
      this.deleteDict(id);
      this.isDeleteDictVisible = false;
      this.isDeleteDictLoaded = false;
    },
    editDict(dict) {
      const currentDict = JSON.parse(JSON.stringify(dict));
      currentDict.pictures = currentDict.images.map(image => {
        if (image) {
          const file = new File([''], image);
          return file;
        } else {
          const emptyFile = new File([''], '');
          return emptyFile;
        }
      });
      currentDict.picturesUrls = currentDict.images.map(image => {
        if (image) {
          return `${process.env.VUE_APP_API_URL}/dicts_images/${image}?token=${this.token}`;
        } else {
          return '';
        }
      });
      delete currentDict.images;
      this.currentDict = currentDict;
      this.currentDict.isNew = false;
      this.isEditingDictVisible = true;
      this.isEditingDictLoaded = true;
    },
    async getDataSourcesHandler() {
      await Promise.all([this.getDataSources({ with_attributes_schema: true }), this.getDataTypes()]);
      this.dataSourcesFetched = true;
    },
    async init() {
      try {
        await this.getDicts();
        this.isFetched = true;
      } catch (error) {
        console.log('init -> error', error);
        this.isFetched = true;
      }
    },
    sortItems() {
      return this.$_objectToArray(this.dicts)?.sort((a, b) => b.id - a.id);
    },
  },
  watch: {
    isImportDictLoaded(nV) {
      if (nV) {
        this.getDataSourcesHandler();
      }
    },
  },
  created() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.layer-element {
  @include border();
  text-align: left;
}
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
